import { useUpdateEffect } from "ahooks";
import { FC, useEffect, useRef, useState } from "react";

import { Icon } from "@/components/icon";
import { runPrefixMethod } from "@/utils/full-screen";

import styles from "./index.module.less";
import { IPictureCourseWareProps } from "./props";

const PictureCourseWare: FC<IPictureCourseWareProps> = ({
  imageData,
  index,
  minScale = 0.5,
  maxScale = 2,
}) => {
  const [currentImage, setCurrentImage] = useState<number>(0);

  const [isFullScreen, setIsFullScreen] = useState(false); // 是否全屏

  const [scale, setScale] = useState(1); // 图片比例

  const imageScreeRef = useRef<HTMLDivElement>(null);

  let downScrollLeft = 0;

  let downClientX = 0;

  let downScrollTop = 0;

  let downClientY = 0;

  let isMoving = false;

  const handleThumbnailClick = (index: number) => {
    setCurrentImage(index);
    setScale(1);
  };

  // 全屏
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    if (!isFullScreen) {
      runPrefixMethod(
        document.querySelector("#image-review-screen"),
        "RequestFullScreen"
      );
    } else {
      setScale(1);
      runPrefixMethod(document, "CancelFullScreen");
    }
  };

  const handleMouseDown = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    isMoving = true;

    if (imageScreeRef.current) {
      downClientX = event.clientX;
      downScrollLeft = imageScreeRef.current.scrollLeft;
      downClientY = event.clientY;
      downScrollTop = imageScreeRef.current.scrollTop;
      imageScreeRef.current.style.cursor = "grab";
    }
  };

  const throttle = <T extends (...args: any[]) => void>(
    callback: T,
    delay: number
  ) => {
    let timeoutId: NodeJS.Timeout | null = null;

    let requestId: number | null = null;

    const throttled = (...args: Parameters<T>) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      requestId = requestAnimationFrame(() => {
        callback(...args);
        requestId = null;
      });
    };

    return (...args: Parameters<T>) => {
      if (!timeoutId && !requestId) {
        timeoutId = setTimeout(() => throttled(...args), delay);
      }
    };
  };

  const scrollCallback = throttle((clientX: number, clientY: number) => {
    if (isMoving && imageScreeRef.current) {
      imageScreeRef.current.scrollLeft =
        downScrollLeft + (downClientX - clientX);
      imageScreeRef.current.scrollTop = downScrollTop + (downClientY - clientY);
    }
  }, 16);

  const handleMouseMove = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => scrollCallback(event.clientX, event.clientY);

  const handleMouseUp = () => {
    isMoving = false;
    if (imageScreeRef.current) {
      imageScreeRef.current.style.cursor = "default";
    }
  };

  // 放大
  const handleZoomIn = () => {
    if (scale < maxScale) setScale((prevScale) => prevScale + 0.1);
  };

  // 缩小
  const handleZoomOut = () => {
    if (scale > minScale) setScale((prevScale) => prevScale - 0.1);
  };

  // 添加监听媒体区域鼠标移动, 控制controls显隐
  useEffect(() => {
    let timer: NodeJS.Timeout;

    const mediaElemnt = document.querySelector("#image-review-screen");

    const controls: HTMLDivElement | null =
      document.querySelector("#image-controls");

    const onDisplayControls = () => {
      clearTimeout(timer);
      if (controls) {
        controls.style.bottom = "0px";
        timer = setTimeout(() => {
          controls.style.bottom = "-1000px";
        }, 5000);
      }
    };

    if (mediaElemnt) {
      mediaElemnt.addEventListener("mousemove", onDisplayControls);
    }

    if (controls) {
      timer = setTimeout(() => {
        controls.style.bottom = "-1000px";
        clearTimeout(timer);
      }, 5000);
    }

    return () => {
      clearTimeout(timer);
      mediaElemnt?.removeEventListener("mousemove", onDisplayControls);
    };
  }, []);
  useUpdateEffect(() => {
    setCurrentImage(index ?? 0);
  }, [index]);

  return (
    <div className={styles["image-container"]}>
      <div className={styles["image-review"]} id="image-review-screen">
        <div
          className={styles["image"]}
          ref={imageScreeRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <img
            src={imageData[currentImage]}
            draggable={false}
            style={{ transform: `scale(${scale})` }}
          />
        </div>

        <div className={styles["controls"]} id="image-controls">
          <Icon
            icon="addCircle"
            width={24}
            height={24}
            onClick={handleZoomIn}
          />
          <Icon
            icon="minusCircle"
            width={24}
            height={24}
            onClick={handleZoomOut}
          />
          <Icon
            icon="screamFull"
            width={24}
            height={24}
            onClick={toggleFullScreen}
          />
        </div>
      </div>
      <div className={styles["thumbnail-list"]}>
        {imageData.map((img, index) => (
          <div
            className={`${styles["thumbnail"]} ${
              currentImage === index && styles["thumbnail-actived"]
            }`}
            key={"img" + index}
          >
            <img
              src={img}
              draggable={false}
              onClick={() => handleThumbnailClick(index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PictureCourseWare;
